import { useCallback, useMemo } from "react";

import { useDispatch, useSelector } from "react-redux";

import { useTranslate } from "@similarweb/i18n-provider";

import { useStorage } from "app/custom-hooks/storage/useStorage";
import type { ColumnPickerPluginConfig } from "app/pages/sales-intelligence/common-components/column-picker";
import { dataCreditsColumnPickerPlugins } from "app/pages/sales-intelligence/modules/quota/plugins/column-picker";
import { useDataCreditsQuery } from "app/pages/sales-intelligence/modules/quota/queries/useDataCreditsQuery";
import {
    selectIsDataCostVisible,
    setDataCostVisibility,
    toggleDataCostVisibility,
} from "app/pages/sales-intelligence/modules/quota/store";
import { technologyColumnPickerPlugins } from "app/pages/sales-intelligence/modules/technology/plugins/column-picker";
import salesSettingsHelper from "app/pages/sales-intelligence/services/salesSettingsHelper";

import { appsSearchSessionStorage } from "../services/storage";
import { appsCostTypeMapper } from "../utils/quota";

type HookValue = {
    config: ColumnPickerPluginConfig | null;
    pluginCleanup: VoidFunction;
};

export const useAppsColumnSelectionPlugins = (): HookValue => {
    const dispatch = useDispatch();
    const { translate } = useTranslate();

    const isDataCostVisible = useSelector(selectIsDataCostVisible);

    const { data, isError } = useDataCreditsQuery({
        select: ({ dataPoints }) => dataPoints,
    });

    const [isSdkColumnHookSubmitted, setIsSdkColumnHookSubmitted] = useStorage(
        appsSearchSessionStorage.sdkColumnHookSubmitted,
    );

    const cleanup = useCallback(() => {
        dispatch(setDataCostVisibility(false));
    }, []);

    const config = useMemo<ColumnPickerPluginConfig | null>(
        () =>
            salesSettingsHelper.isDataCreditsEnabled && !isError && data
                ? {
                      columnPlugins: [
                          dataCreditsColumnPickerPlugins.createCostBadge({
                              dataPoints: data,
                              isCostVisible: isDataCostVisible,
                              costTypeMapper: appsCostTypeMapper,
                          }),
                          technologyColumnPickerPlugins.createSdkUpgradeButton({
                              isHookSubmitted: isSdkColumnHookSubmitted,
                              onHookSubmit: () => setIsSdkColumnHookSubmitted(true),
                          }),
                      ],
                      headerPlugins: [
                          dataCreditsColumnPickerPlugins.createCostToggle({
                              isChecked: isDataCostVisible,
                              onToggle: () => dispatch(toggleDataCostVisibility()),
                          }),
                      ],
                      listPlugins: [
                          dataCreditsColumnPickerPlugins.createTotalCostBadge({
                              dataPoints: data,
                              isCostVisible: isDataCostVisible,
                              costTypeMapper: appsCostTypeMapper,
                              translate: translate,
                          }),
                      ],
                  }
                : null,
        [isError, data, isDataCostVisible, dispatch, translate, isSdkColumnHookSubmitted, setIsSdkColumnHookSubmitted],
    );

    return { config, pluginCleanup: cleanup };
};
